import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { FileText, Shield, Users, Loader2, Lock, Ticket } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(true);
    const [registerName, setRegisterName] = useState('');
    const [registerEmail, setRegisterEmail] = useState('');
    const [registerPassword, setRegisterPassword] = useState('');
    const [inviteCode, setInviteCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    
    // 2FA states
    const [show2FA, setShow2FA] = useState(false);
    const [twoFACode, setTwoFACode] = useState('');
    const [twoFAEmail, setTwoFAEmail] = useState('');
    const [resending, setResending] = useState(false);
    
    // Forgot password states
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotSent, setForgotSent] = useState(false);
    
    const { login, register, setToken, setUser } = useAuth();
    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const response = await axios.post(`${API_URL}/api/auth/login`, {
                email: loginEmail,
                password: loginPassword,
                remember_me: rememberMe
            });
            
            const data = response.data;
            
            // Check if 2FA is required
            if (data.requires_2fa) {
                setTwoFAEmail(loginEmail);
                setShow2FA(true);
                toast.info('Verification code sent to your email');
            } else {
                // Normal login - set auth
                localStorage.setItem('token', data.access_token);
                setToken(data.access_token);
                setUser(data.user);
                toast.success(rememberMe ? 'Welcome back! You\'ll stay logged in for 30 days.' : 'Welcome back!');
                navigate('/dashboard');
            }
        } catch (error) {
            const status = error.response?.status;
            const detail = error.response?.data?.detail || 'Login failed';
            
            if (status === 429) {
                // Rate limited
                toast.error(detail, { duration: 5000 });
            } else if (status === 423) {
                // Account locked
                toast.error(detail, { duration: 5000 });
            } else if (status === 403) {
                // IP blocked
                toast.error('Access denied from your location');
            } else {
                toast.error(detail);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerify2FA = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const response = await axios.post(`${API_URL}/api/auth/verify-2fa`, {
                email: twoFAEmail,
                code: twoFACode,
                remember_me: rememberMe
            });
            
            const data = response.data;
            localStorage.setItem('token', data.access_token);
            setToken(data.access_token);
            setUser(data.user);
            toast.success('Verification successful! Welcome back!');
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Invalid verification code');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResend2FA = async () => {
        setResending(true);
        try {
            await axios.post(`${API_URL}/api/auth/resend-2fa`, { email: twoFAEmail });
            toast.success('New verification code sent!');
        } catch (error) {
            toast.error('Failed to resend code');
        } finally {
            setResending(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        if (!inviteCode.trim()) {
            toast.error('Invitation code is required');
            return;
        }
        setIsLoading(true);
        try {
            const response = await axios.post(`${API_URL}/api/auth/register?invite_code=${inviteCode}`, {
                name: registerName,
                email: registerEmail,
                password: registerPassword,
                role: 'staff'
            });
            
            const data = response.data;
            
            // 2FA is mandatory - show 2FA screen
            if (data.requires_2fa) {
                setTwoFAEmail(registerEmail);
                setShow2FA(true);
                toast.info('Account created! Please verify with the code sent to your email.');
            } else {
                localStorage.setItem('token', data.access_token);
                setToken(data.access_token);
                setUser(data.user);
                toast.success('Account created successfully!');
                navigate('/dashboard');
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Registration failed');
        } finally {
            setIsLoading(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        if (!forgotEmail.trim()) {
            toast.error('Please enter your email');
            return;
        }
        setIsLoading(true);
        try {
            await axios.post(`${API_URL}/api/auth/forgot-password`, { email: forgotEmail });
            setForgotSent(true);
            toast.success('If an account exists, a reset link has been sent.');
        } catch (error) {
            toast.error('Failed to send reset email');
        } finally {
            setIsLoading(false);
        }
    };

    const features = [
        { icon: FileText, title: 'Document Generation', desc: 'Template-based legal documents' },
        { icon: Shield, title: 'Secure Data', desc: 'Protected client information' },
        { icon: Users, title: 'Client Management', desc: 'Streamlined intake process' },
    ];

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            {/* Left side - Hero */}
            <div 
                className="hidden lg:flex flex-col justify-between p-12 relative"
                style={{
                    backgroundImage: 'linear-gradient(to bottom, rgba(28, 25, 23, 0.92), rgba(28, 25, 23, 0.98)), url(https://images.pexels.com/photos/5673478/pexels-photo-5673478.jpeg)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}
            >
                <div>
                    <div className="flex items-center gap-3 text-white">
                        <img src="/lion-logo.png" alt="BestWillsAndTrusts" className="h-12 w-12 object-contain" />
                        <span className="font-serif text-2xl font-semibold tracking-tight text-amber-400">BestWillsAndTrusts</span>
                    </div>
                </div>
                
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="space-y-8"
                >
                    <h1 className="font-serif text-4xl lg:text-5xl text-white leading-tight">
                        Streamline Your Estate Planning Practice
                    </h1>
                    <p className="text-white/80 text-lg max-w-md">
                        A comprehensive intake system designed by and for BestWillsAndTrusts.com.
                    </p>
                    
                    <div className="space-y-4 pt-8">
                        {features.map((feature, index) => (
                            <motion.div 
                                key={feature.title}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.3 + index * 0.1 }}
                                className="flex items-center gap-4 text-white"
                            >
                                <div className="p-2 bg-white/10 backdrop-blur-sm">
                                    <feature.icon className="h-5 w-5" />
                                </div>
                                <div>
                                    <p className="font-medium">{feature.title}</p>
                                    <p className="text-sm text-white/70">{feature.desc}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
                
                <p className="text-white/50 text-sm">
                    © 2026 BestWillsAndTrusts. Professional Estate Planning Software.
                </p>
            </div>

            {/* Right side - Auth forms */}
            <div className="flex items-center justify-center p-8 bg-[#FDFCF8]">
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="w-full max-w-md"
                >
                    <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
                        <img src="/lion-logo.png" alt="BestWillsAndTrusts" className="h-10 w-10 object-contain" />
                        <span className="font-serif text-xl font-semibold text-amber-600">BestWillsAndTrusts</span>
                    </div>

                    <Tabs defaultValue="login" className="w-full">
                        <TabsList className="grid w-full grid-cols-2 mb-8 rounded-none bg-secondary">
                            <TabsTrigger 
                                value="login" 
                                className="rounded-none data-[state=active]:bg-white data-[state=active]:text-primary font-medium"
                                data-testid="login-tab"
                            >
                                Sign In
                            </TabsTrigger>
                            <TabsTrigger 
                                value="register" 
                                className="rounded-none data-[state=active]:bg-white data-[state=active]:text-primary font-medium"
                                data-testid="register-tab"
                            >
                                Register
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="login">
                            {!show2FA && !showForgotPassword ? (
                            <Card className="border-stone-200 shadow-sm rounded-none">
                                <CardHeader className="space-y-1">
                                    <CardTitle className="font-serif text-2xl">Welcome back</CardTitle>
                                    <CardDescription>
                                        Enter your credentials to access your account
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <form onSubmit={handleLogin} className="space-y-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="login-email">Email</Label>
                                            <Input
                                                id="login-email"
                                                type="email"
                                                placeholder="attorney@lawfirm.com"
                                                value={loginEmail}
                                                onChange={(e) => setLoginEmail(e.target.value)}
                                                required
                                                className="rounded-none"
                                                data-testid="login-email-input"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="login-password">Password</Label>
                                            <Input
                                                id="login-password"
                                                type="password"
                                                value={loginPassword}
                                                onChange={(e) => setLoginPassword(e.target.value)}
                                                required
                                                className="rounded-none"
                                                data-testid="login-password-input"
                                            />
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-2">
                                                <Checkbox 
                                                    id="remember-me" 
                                                    checked={rememberMe}
                                                    onCheckedChange={(checked) => setRememberMe(checked)}
                                                    className="rounded-none"
                                                    data-testid="remember-me-checkbox"
                                                />
                                                <Label 
                                                    htmlFor="remember-me" 
                                                    className="text-sm font-normal cursor-pointer"
                                                >
                                                    Stay logged in for 30 days
                                                </Label>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setShowForgotPassword(true)}
                                                className="text-sm text-amber-600 hover:text-amber-700 font-medium"
                                                data-testid="forgot-password-link"
                                            >
                                                Forgot password?
                                            </button>
                                        </div>
                                        <Button 
                                            type="submit" 
                                            className="w-full rounded-none font-medium uppercase tracking-wide"
                                            disabled={isLoading}
                                            data-testid="login-submit-btn"
                                        >
                                            {isLoading ? 'Signing in...' : 'Sign In'}
                                        </Button>
                                    </form>
                                </CardContent>
                            </Card>
                            ) : showForgotPassword ? (
                            <Card className="border-stone-200 shadow-sm rounded-none">
                                <CardHeader className="space-y-1">
                                    <CardTitle className="font-serif text-2xl">Reset Password</CardTitle>
                                    <CardDescription>
                                        {forgotSent 
                                            ? "Check your email for a password reset link."
                                            : "Enter your email address and we'll send you a reset link."
                                        }
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    {!forgotSent ? (
                                        <form onSubmit={handleForgotPassword} className="space-y-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="forgot-email">Email</Label>
                                                <Input
                                                    id="forgot-email"
                                                    type="email"
                                                    placeholder="attorney@lawfirm.com"
                                                    value={forgotEmail}
                                                    onChange={(e) => setForgotEmail(e.target.value)}
                                                    required
                                                    className="rounded-none"
                                                    data-testid="forgot-email-input"
                                                />
                                            </div>
                                            <Button 
                                                type="submit" 
                                                className="w-full rounded-none font-medium uppercase tracking-wide"
                                                disabled={isLoading}
                                                data-testid="forgot-submit-btn"
                                            >
                                                {isLoading ? 'Sending...' : 'Send Reset Link'}
                                            </Button>
                                            <button
                                                type="button"
                                                onClick={() => setShowForgotPassword(false)}
                                                className="w-full text-sm text-stone-500 hover:text-stone-700"
                                            >
                                                ← Back to login
                                            </button>
                                        </form>
                                    ) : (
                                        <div className="space-y-4">
                                            <div className="p-4 bg-green-50 border border-green-200 text-green-800 text-sm">
                                                <strong>Email sent!</strong> Check your inbox for the password reset link. It will expire in 1 hour.
                                            </div>
                                            <Button 
                                                type="button"
                                                variant="outline"
                                                onClick={handleForgotPassword}
                                                disabled={isLoading}
                                                className="w-full rounded-none"
                                                data-testid="resend-reset-email-btn"
                                            >
                                                {isLoading ? 'Sending...' : 'Resend Reset Email'}
                                            </Button>
                                            <button
                                                type="button"
                                                onClick={() => { setShowForgotPassword(false); setForgotSent(false); setForgotEmail(''); }}
                                                className="w-full text-sm text-stone-500 hover:text-stone-700"
                                            >
                                                ← Back to login
                                            </button>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                            ) : show2FA ? (
                            <Card className="border-stone-200 shadow-sm rounded-none">
                                <CardHeader className="space-y-1">
                                    <div className="flex items-center gap-2 text-amber-600 mb-2">
                                        <Lock className="h-5 w-5" />
                                        <span className="text-sm font-medium uppercase tracking-wide">Two-Factor Authentication</span>
                                    </div>
                                    <CardTitle className="font-serif text-2xl">Verify Your Identity</CardTitle>
                                    <CardDescription>
                                        We've sent a 6-digit verification code to <strong>{twoFAEmail}</strong>
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <form onSubmit={handleVerify2FA} className="space-y-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="2fa-code">Verification Code</Label>
                                            <Input
                                                id="2fa-code"
                                                type="text"
                                                placeholder="Enter 6-digit code"
                                                value={twoFACode}
                                                onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                                required
                                                maxLength={6}
                                                className="rounded-none text-center text-2xl tracking-[0.5em] font-mono"
                                                data-testid="2fa-code-input"
                                            />
                                        </div>
                                        <Button 
                                            type="submit" 
                                            className="w-full rounded-none font-medium uppercase tracking-wide"
                                            disabled={isLoading || twoFACode.length !== 6}
                                            data-testid="2fa-verify-btn"
                                        >
                                            {isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                                            {isLoading ? 'Verifying...' : 'Verify & Sign In'}
                                        </Button>
                                        <div className="flex items-center justify-between text-sm">
                                            <button 
                                                type="button"
                                                onClick={() => { setShow2FA(false); setTwoFACode(''); }}
                                                className="text-stone-500 hover:text-stone-700"
                                            >
                                                ← Back to login
                                            </button>
                                            <button 
                                                type="button"
                                                onClick={handleResend2FA}
                                                disabled={resending}
                                                className="text-amber-600 hover:text-amber-700 font-medium"
                                            >
                                                {resending ? 'Sending...' : 'Resend code'}
                                            </button>
                                        </div>
                                    </form>
                                </CardContent>
                            </Card>
                            ) : null}
                        </TabsContent>

                        <TabsContent value="register">
                            <Card className="border-stone-200 shadow-sm rounded-none">
                                <CardHeader className="space-y-1">
                                    <CardTitle className="font-serif text-2xl">Create account</CardTitle>
                                    <CardDescription>
                                        Registration requires an invitation code from your administrator
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <form onSubmit={handleRegister} className="space-y-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="invite-code" className="flex items-center gap-2">
                                                <Ticket className="h-4 w-4 text-amber-600" />
                                                Invitation Code
                                            </Label>
                                            <Input
                                                id="invite-code"
                                                type="text"
                                                placeholder="Enter your invitation code"
                                                value={inviteCode}
                                                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                                                required
                                                className="rounded-none font-mono uppercase tracking-wider"
                                                data-testid="invite-code-input"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="register-name">Full Name</Label>
                                            <Input
                                                id="register-name"
                                                type="text"
                                                placeholder="Jane Smith, Esq."
                                                value={registerName}
                                                onChange={(e) => setRegisterName(e.target.value)}
                                                required
                                                className="rounded-none"
                                                data-testid="register-name-input"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="register-email">Email</Label>
                                            <Input
                                                id="register-email"
                                                type="email"
                                                placeholder="attorney@lawfirm.com"
                                                value={registerEmail}
                                                onChange={(e) => setRegisterEmail(e.target.value)}
                                                required
                                                className="rounded-none"
                                                data-testid="register-email-input"
                                            />
                                            <p className="text-xs text-stone-500">
                                                Must match the email on your invitation
                                            </p>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="register-password">Password</Label>
                                            <Input
                                                id="register-password"
                                                type="password"
                                                value={registerPassword}
                                                onChange={(e) => setRegisterPassword(e.target.value)}
                                                required
                                                className="rounded-none"
                                                data-testid="register-password-input"
                                            />
                                            <p className="text-xs text-stone-500">
                                                Min 8 characters with uppercase, lowercase, number & special character
                                            </p>
                                        </div>
                                        <Button 
                                            type="submit" 
                                            className="w-full rounded-none font-medium uppercase tracking-wide"
                                            disabled={isLoading}
                                            data-testid="register-submit-btn"
                                        >
                                            {isLoading ? 'Creating account...' : 'Create Account'}
                                        </Button>
                                    </form>
                                </CardContent>
                            </Card>
                        </TabsContent>
                    </Tabs>
                </motion.div>
            </div>
        </div>
    );
};

export default LoginPage;
